import { DisplayLogo } from "@/components/Common/DisplayLogo"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@okeys/shared-react-components"
import {
  DailySchedule,
  DataShopTable,
  getColorInfo,
  Schedule,
} from "@okeys/shared-utils"
import { Pencil2Icon, PlusIcon } from "@radix-ui/react-icons"
import { format } from "date-fns"
import React, { useState } from "react"
import { AddImageModal } from "../../components/ShopSettings/modals/AddImageModal"
import { DeleteImageModal } from "../../components/ShopSettings/modals/DeleteImageModal"
import { EditBooleanFieldModal } from "../../components/ShopSettings/modals/EditBooleanFieldModal"
import { EditFidelityModal } from "../../components/ShopSettings/modals/EditFidelityModal"
import { EditHashtagsModal } from "../../components/ShopSettings/modals/EditHashtagsModal"
import { EditLogoModal } from "../../components/ShopSettings/modals/EditLogoModal"
import { EditReservationOptionsModal } from "../../components/ShopSettings/modals/EditReservationOptionsModal"
import { EditScheduleModal } from "../../components/ShopSettings/modals/EditScheduleModal"
import { EditThemeColorModal } from "../../components/ShopSettings/modals/EditThemeColorModal"
import { ShopImage } from "../../components/ShopSettings/ShopImage"
import { useShopDataQuery } from "../../query/shopDataQuery"

export type TimeSlot = {
  start: string
  end: string
}

type EditableField =
  | "hashtags"
  | "withPhoto"
  | "fidelity"
  | "themeColor"
  | "schedule"
  | "reservationOptions"
  | "commandOptions"
  | null

// Composant utilitaire pour afficher une information
const InfoItem = ({
  label,
  value,
  className,
  canModify = false,
  fieldId,
  onEditClick,
}: {
  label: string
  value: React.ReactNode
  className?: string
  canModify?: boolean
  fieldId?: EditableField
  onEditClick?: (field: EditableField) => void
}) => (
  <div
    className={`group relative py-3 px-4 rounded-md transition-colors duration-150 ${
      className || "hover:bg-slate-50 dark:hover:bg-slate-800/50"
    }`}
  >
    <div className="flex-1 min-w-0">
      <p
        className="text-sm font-medium text-slate-600 dark:text-slate-300 truncate"
        title={label}
      >
        {label}
      </p>
      <div className="text-md text-slate-800 dark:text-slate-100 mt-0.5 font-semibold break-words">
        {typeof value === "boolean" ? (
          value ? (
            <span className="text-green-600 dark:text-green-400">Oui</span>
          ) : (
            <span className="text-red-600 dark:text-red-400">Non</span>
          )
        ) : (
          value
        )}
      </div>
    </div>
    {canModify && fieldId && onEditClick && (
      <button
        className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-full text-blue-500 hover:text-blue-700 dark:hover:text-blue-300 opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity duration-150 focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"
        onClick={() => onEditClick(fieldId)}
        aria-label={`Modifier ${label}`}
      >
        <Pencil2Icon className="h-5 w-5" />
      </button>
    )}
  </div>
)

export const ShopSettings = () => {
  const { shopData, pendingShopData, errorShopData, refetchShopData } =
    useShopDataQuery()
  const [editingField, setEditingField] = useState<EditableField>(null)
  const [isAddImageModalOpen, setIsAddImageModalOpen] = useState(false)
  const [deleteImageData, setDeleteImageData] = useState<{
    isOpen: boolean
    imageIndex: number
    imagePath: string
  }>({
    isOpen: false,
    imageIndex: -1,
    imagePath: "",
  })
  const [isEditLogoModalOpen, setIsEditLogoModalOpen] = useState(false)
  const [isReservationOptionsModalOpen, setIsReservationOptionsModalOpen] =
    useState(false)

  const handleEditClick = (field: EditableField) => {
    if (field === "reservationOptions") {
      setIsReservationOptionsModalOpen(true)
    } else {
      setEditingField(field)
    }
  }

  const handleCloseModal = () => {
    setEditingField(null)
  }

  const handleAddImageClick = () => {
    setIsAddImageModalOpen(true)
  }

  const handleCloseAddImageModal = () => {
    setIsAddImageModalOpen(false)
  }

  const handleDeleteImageClick = (imageIndex: number, imagePath: string) => {
    setDeleteImageData({
      isOpen: true,
      imageIndex,
      imagePath,
    })
  }

  const handleCloseDeleteImageModal = () => {
    setDeleteImageData({
      isOpen: false,
      imageIndex: -1,
      imagePath: "",
    })
  }

  const handleEditLogoClick = () => {
    setIsEditLogoModalOpen(true)
  }

  const handleCloseEditLogoModal = () => {
    setIsEditLogoModalOpen(false)
  }

  // Fonction utilitaire pour normaliser les hashtags
  const normalizeHashtags = (hashtags: unknown): string[] => {
    if (!hashtags || !Array.isArray(hashtags)) return []

    // Si c'est un tableau de tableaux, on l'aplatit
    if (hashtags.length > 0 && Array.isArray(hashtags[0])) {
      return (hashtags as string[][]).flat()
    }

    // Sinon, on assume que c'est déjà un tableau de strings
    return hashtags as string[]
  }

  const handleSave = (
    field: EditableField,
    newData:
      | DataShopTable[keyof DataShopTable]
      | { street: string; city: string; postalCode: string }
      | string[]
      | string
      | boolean
      | { fidelityNumber?: number }
      | { color?: string }
      | Schedule
  ) => {
    // Logique de mutation spécifique au champ
    if (field === "hashtags" && Array.isArray(newData)) {
    } else if (field === "withPhoto" && typeof newData === "boolean") {
    } else if (
      field === "fidelity" &&
      typeof newData === "object" &&
      newData !== null &&
      "fidelityNumber" in newData
    ) {
    } else if (
      field === "themeColor" &&
      typeof newData === "object" &&
      newData !== null &&
      "color" in newData
    ) {
    } else if (field === "schedule" && typeof newData === "object") {
    }

    // Vous pourriez vouloir refetchShopData() après une sauvegarde réussie
    handleCloseModal()
  }

  if (pendingShopData) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50 dark:bg-slate-900">
        <div className="flex flex-col items-center">
          <div className="w-12 h-12 border-4 border-slate-300 border-t-slate-600 rounded-full animate-spin mb-4"></div>
          <p className="text-slate-600 dark:text-slate-300 text-lg">
            Chargement des données de la boutique...
          </p>
        </div>
      </div>
    )
  }

  if (errorShopData) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-red-50 dark:bg-red-900/10 p-4">
        <Alert variant="destructive" className="max-w-lg w-full shadow-lg">
          <AlertTitle className="text-lg font-semibold">
            Erreur de Connexion
          </AlertTitle>
          <AlertDescription className="mt-2">
            Impossible de charger les informations de la boutique pour le
            moment. Veuillez vérifier votre connexion internet ou réessayer plus
            tard.
            <div className="mt-4">
              <Button
                onClick={() => refetchShopData()}
                className="w-full sm:w-auto"
              >
                Réessayer de charger
              </Button>
            </div>
          </AlertDescription>
        </Alert>
      </div>
    )
  }

  if (!shopData) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50 dark:bg-slate-900 p-4">
        <Alert className="max-w-md w-full shadow-md">
          <AlertTitle className="text-lg font-medium">Aucune Donnée</AlertTitle>
          <AlertDescription className="mt-1">
            Les informations de cette boutique ne sont pas disponibles
            actuellement.
          </AlertDescription>
        </Alert>
      </div>
    )
  }

  const typedShopData = shopData as DataShopTable

  const formatDate = (dateString: string | number | Date) => {
    try {
      return format(new Date(dateString), "dd/MM/yyyy 'à' HH:mm:ss")
    } catch (e) {
      return String(dateString)
    }
  }

  // Fonction pour traduire les jours en français
  const translateDay = (day: string): string => {
    const translations: { [key: string]: string } = {
      monday: "Lundi",
      tuesday: "Mardi",
      wednesday: "Mercredi",
      thursday: "Jeudi",
      friday: "Vendredi",
      saturday: "Samedi",
      sunday: "Dimanche",
    }
    return translations[day.toLowerCase()] || day
  }

  // Fonction pour déterminer si un jour est ouvert
  const isDayOpen = (slots: DailySchedule | undefined): boolean => {
    return Boolean(slots && slots.length > 0)
  }

  return (
    <div className="h-full flex flex-col">
      <div className="overflow-y-auto space-y-4 flex-1 min-h-0 flex flex-col">
        <Card>
          <CardHeader className="flex flex-row gap-4">
            <CardTitle>Informations Générales</CardTitle>

            {/* Logo éditable avec effet de survol */}
            <div
              className="group relative cursor-pointer"
              onClick={handleEditLogoClick}
            >
              <div className="relative overflow-hidden rounded-full transition-all duration-300 group-hover:shadow-lg">
                <DisplayLogo
                  urlLogo={typedShopData.urlLogo}
                  shopName={typedShopData.shopName}
                  size="medium"
                />

                {/* Overlay avec effet d'assombrissement */}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all duration-300 flex items-center justify-center rounded-full">
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-transparent/90 dark:bg-slate-800/90 rounded-full p-3 shadow-lg backdrop-blur-sm">
                    <Pencil2Icon className="w-5 h-5 text-white dark:text-slate-200" />
                  </div>
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
            <InfoItem
              label="Type de boutique"
              value={typedShopData.type}
              canModify={false}
              className="hover:bg-blue-50/50"
            />
            <InfoItem
              label="Nom de la boutique"
              value={typedShopData.shopName}
              canModify={false}
              className="hover:bg-blue-50/50"
            />
            <InfoItem
              label="Adresse complète"
              value={`${typedShopData.street}, ${typedShopData.postalCode} ${typedShopData.city}`}
              canModify={false}
              className="hover:bg-blue-50/50"
            />
            <InfoItem
              label="Date de création"
              value={formatDate(typedShopData.creationDate)}
              canModify={false}
              className="hover:bg-blue-50/50"
            />
            {typedShopData.hashtags && typedShopData.hashtags.length > 0 && (
              <InfoItem
                label="Domaines d'activité"
                value={
                  <div className="flex flex-wrap gap-2">
                    {normalizeHashtags(typedShopData.hashtags).map(
                      (tag: string) => (
                        <Badge
                          key={tag}
                          variant="outline"
                          className="border-blue-500 text-blue-600 dark:border-blue-400 dark:text-blue-300 bg-blue-50 dark:bg-blue-500/10 px-3 py-1 text-xs font-medium"
                        >
                          {tag}
                        </Badge>
                      )
                    )}
                  </div>
                }
                canModify={true}
                fieldId="hashtags"
                onEditClick={handleEditClick}
                className="md:col-span-2 hover:bg-blue-50/50"
              />
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Configuration de la boutique</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
              {typedShopData.hasFidelity && (
                <InfoItem
                  label="Programme de fidélité"
                  value={`${typedShopData.fidelityNumber} points requis`}
                  canModify={true}
                  fieldId="fidelity"
                  onEditClick={handleEditClick}
                  className="hover:bg-green-50/50"
                />
              )}
              {typedShopData.theme && typedShopData.theme.color && (
                <InfoItem
                  label="Thème de couleur Okeys"
                  value={
                    <Badge
                      style={{
                        backgroundColor: getColorInfo(typedShopData.theme.color)
                          ?.primary,
                        color:
                          typedShopData.theme.color.toLowerCase() === "white" ||
                          typedShopData.theme.color.toLowerCase() ===
                            "yellow" ||
                          typedShopData.theme.color.toLowerCase() === "#ffffff"
                            ? "#000000"
                            : "#ffffff",
                        border: "1px solid rgba(0,0,0,0.1)",
                      }}
                      className="px-3 py-1 text-xs font-bold shadow-sm tracking-wide"
                    >
                      {getColorInfo(typedShopData.theme.color)?.name.replace(
                        "_",
                        " "
                      )}
                    </Badge>
                  }
                  canModify={true}
                  fieldId="themeColor"
                  onEditClick={handleEditClick}
                  className="hover:bg-green-50/50"
                />
              )}
            </div>
          </CardContent>
        </Card>

        {/* Carte pour les options de réservation */}
        <Card>
          <CardHeader>
            <CardTitle>Options de Réservation</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
              <InfoItem
                label="Montant des frais d'annulation"
                value={
                  typedShopData.bookingOptions?.cancellationFee
                    ? `${typedShopData.bookingOptions.cancellationFee}€`
                    : "Aucun frais"
                }
                canModify={true}
                fieldId="reservationOptions"
                onEditClick={handleEditClick}
                className="hover:bg-indigo-50/50"
              />
              <InfoItem
                label="Délai d'annulation"
                value={`${
                  typedShopData.bookingOptions?.cancellationNoticeDays ?? 1
                } jour(s) avant`}
                canModify={true}
                fieldId="reservationOptions"
                onEditClick={handleEditClick}
                className="hover:bg-indigo-50/50"
              />
              <InfoItem
                label="Pourcentage de prépaiement"
                value={
                  typedShopData.bookingOptions?.percentagePrepayment
                    ? `${typedShopData.bookingOptions.percentagePrepayment}%`
                    : "Aucun prépaiement"
                }
                canModify={true}
                fieldId="reservationOptions"
                onEditClick={handleEditClick}
                className="hover:bg-indigo-50/50"
              />
              <InfoItem
                label="Réservation sans paiement"
                value={
                  typedShopData.bookingOptions?.canReserveWithoutPayment ??
                  false
                }
                canModify={true}
                fieldId="reservationOptions"
                onEditClick={handleEditClick}
                className="hover:bg-indigo-50/50"
              />
            </div>
          </CardContent>
        </Card>

        {typedShopData.urlsImage && typedShopData.urlsImage.length > 0 ? (
          <>
            <Card>
              <CardHeader>
                <CardTitle>Galerie d'images</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {typedShopData.urlsImage.map(
                    (urlPath: string, index: number) => (
                      <div
                        key={index}
                        className="transform transition-transform duration-300 hover:-translate-y-1"
                      >
                        <ShopImage
                          initialImagePath={urlPath}
                          altText={`Image ${index + 1} de la boutique`}
                          allImagePaths={typedShopData.urlsImage}
                          initialImageIndex={index}
                          onDelete={handleDeleteImageClick}
                        />
                      </div>
                    )
                  )}

                  {/* Carte pour ajouter une nouvelle image */}
                  <div className="transform transition-transform duration-300 hover:-translate-y-1">
                    <div
                      onClick={handleAddImageClick}
                      className="group relative aspect-[4/3] w-full overflow-hidden rounded-xl bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border-2 border-dashed border-blue-300 dark:border-blue-600 hover:border-blue-500 dark:hover:border-blue-400 shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer"
                    >
                      <div className="absolute inset-0 flex flex-col items-center justify-center space-y-3">
                        <div className="w-12 h-12 rounded-full bg-blue-100 dark:bg-blue-800/50 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                          <PlusIcon className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                        </div>
                        <div className="text-center">
                          <p className="text-sm font-medium text-blue-700 dark:text-blue-300">
                            Ajouter une image
                          </p>
                          <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                            Cliquez pour sélectionner
                          </p>
                        </div>
                      </div>

                      {/* Effet de survol */}
                      <div className="absolute inset-0 bg-blue-500/0 group-hover:bg-blue-500/10 transition-all duration-300"></div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </>
        ) : (
          // Section pour quand il n'y a pas d'images
          <>
            <Card>
              <CardHeader>
                <CardTitle>Galerie d'images</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-center py-12">
                  <div className="w-20 h-20 mx-auto rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center mb-4 text-4xl">
                    📷
                  </div>
                  <h3 className="text-lg font-medium text-slate-700 dark:text-slate-200 mb-2">
                    Aucune image dans la galerie
                  </h3>
                  <p className="text-slate-500 dark:text-slate-400 mb-6 max-w-md mx-auto">
                    Ajoutez des images de votre boutique pour donner une
                    meilleure impression à vos clients.
                  </p>
                  <Button
                    onClick={handleAddImageClick}
                    className="bg-blue-500 hover:bg-blue-600 text-white"
                  >
                    <PlusIcon className="h-4 w-4 mr-2" />
                    Ajouter votre première image
                  </Button>
                </div>
              </CardContent>
            </Card>
          </>
        )}

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Horaires d'Ouverture Généraux</CardTitle>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleEditClick("schedule")}
                className="flex items-center gap-2 bg-black hover:bg-black/80 text-white hover:text-white dark:bg-white dark:hover:bg-white/80 dark:hover:text-black"
              >
                <Pencil2Icon className="h-4 w-4" />
                Modifier
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {Object.entries(typedShopData.shopSchedule).map(
                ([day, slots]) => {
                  const currentSlots = slots as DailySchedule | undefined
                  const isOpen = isDayOpen(currentSlots)

                  return (
                    <div
                      key={day}
                      className={`relative p-4 rounded-xl border-2 transition-all duration-300 hover:shadow-md ${
                        isOpen
                          ? "border-green-200 dark:border-green-800 bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20"
                          : "border-red-200 dark:border-red-800 bg-gradient-to-br from-red-50 to-rose-50 dark:from-red-900/20 dark:to-rose-900/20"
                      }`}
                    >
                      {/* Indicateur de statut */}
                      <div className="absolute top-3 right-3">
                        <div
                          className={`w-3 h-3 rounded-full ${
                            isOpen
                              ? "bg-green-400 shadow-green-400/50"
                              : "bg-red-400 shadow-red-400/50"
                          } shadow-lg`}
                        ></div>
                      </div>

                      {/* Jour de la semaine */}
                      <div className="mb-3">
                        <h3 className="font-bold text-slate-800 dark:text-slate-100 text-lg">
                          {translateDay(day)}
                        </h3>
                      </div>

                      {/* Horaires */}
                      <div className="space-y-2">
                        {isOpen ? (
                          currentSlots?.map((slot: TimeSlot, index: number) => (
                            <div
                              key={index}
                              className="flex items-center justify-between p-2 bg-white/60 dark:bg-slate-800/60 rounded-lg border border-green-200/50 dark:border-green-700/50"
                            >
                              <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                                {slot.start}
                              </span>
                              <div className="text-slate-500 dark:text-slate-400 text-xs">
                                →
                              </div>
                              <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                                {slot.end}
                              </span>
                            </div>
                          ))
                        ) : (
                          <div className="flex items-center justify-center p-3 bg-white/60 dark:bg-slate-800/60 rounded-lg border border-red-200/50 dark:border-red-700/50">
                            <span className="text-sm font-medium text-red-600 dark:text-red-400">
                              Fermé
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Badge de statut */}
                      <div className="mt-3 flex justify-center">
                        <span
                          className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                            isOpen
                              ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300"
                              : "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300"
                          }`}
                        >
                          {isOpen ? "Ouvert" : "Fermé"}
                        </span>
                      </div>
                    </div>
                  )
                }
              )}
            </div>

            {/* Résumé des horaires */}
            <div className="mt-6 p-4 bg-gradient-to-r from-orange-50 to-amber-50 dark:from-orange-900/20 dark:to-amber-900/20 border border-orange-200 dark:border-orange-800 rounded-lg">
              <div>
                <p className="text-sm font-medium text-orange-800 dark:text-orange-200">
                  Résumé de la semaine
                </p>
                <p className="text-sm text-orange-700 dark:text-orange-300 mt-1">
                  {
                    Object.values(typedShopData.shopSchedule).filter((slots) =>
                      isDayOpen(slots as DailySchedule | undefined)
                    ).length
                  }{" "}
                  jours d'ouverture sur 7
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Modale pour les hashtags */}
      {editingField === "hashtags" && typedShopData && (
        <EditHashtagsModal
          isOpen={editingField === "hashtags"}
          currentHashtags={normalizeHashtags(typedShopData.hashtags)}
          onSave={(newHashtags) => handleSave("hashtags", newHashtags)}
          onClose={handleCloseModal}
        />
      )}

      {/* Modale pour withPhoto */}
      {editingField === "withPhoto" && typedShopData && (
        <EditBooleanFieldModal
          isOpen={editingField === "withPhoto"}
          fieldName="l'affichage des photos de la boutique"
          fieldDescription="Activez cette option pour afficher les photos de votre boutique dans l'application client."
          currentValue={typedShopData.withPhoto}
          onSave={(newValue) => handleSave("withPhoto", newValue)}
          onClose={handleCloseModal}
        />
      )}

      {/* Modale pour fidelity */}
      {editingField === "fidelity" &&
        typedShopData &&
        typedShopData.hasFidelity && (
          <EditFidelityModal
            isOpen={editingField === "fidelity"}
            currentFidelityNumber={typedShopData.fidelityNumber}
            onClose={handleCloseModal}
          />
        )}

      {/* Modale pour themeColor */}
      {editingField === "themeColor" &&
        typedShopData &&
        typedShopData.theme && (
          <EditThemeColorModal
            isOpen={editingField === "themeColor"}
            currentColor={typedShopData.theme.color}
            onSave={(newThemeData) => handleSave("themeColor", newThemeData)}
            onClose={handleCloseModal}
          />
        )}

      {/* Modale pour ajouter une image */}
      <AddImageModal
        isOpen={isAddImageModalOpen}
        onClose={handleCloseAddImageModal}
      />

      {/* Modale pour supprimer une image */}
      <DeleteImageModal
        isOpen={deleteImageData.isOpen}
        imageIndex={deleteImageData.imageIndex}
        imagePath={deleteImageData.imagePath}
        onClose={handleCloseDeleteImageModal}
      />

      {/* Modale pour modifier les horaires */}
      {editingField === "schedule" && typedShopData && (
        <EditScheduleModal
          isOpen={editingField === "schedule"}
          currentSchedule={typedShopData.shopSchedule}
          onSave={(newSchedule: Schedule) =>
            handleSave("schedule", newSchedule)
          }
          onClose={handleCloseModal}
        />
      )}

      {/* Modale pour modifier le logo */}
      <EditLogoModal
        isOpen={isEditLogoModalOpen}
        currentLogoUrl={typedShopData.urlLogo}
        shopName={typedShopData.shopName}
        onClose={handleCloseEditLogoModal}
      />

      {/* Modale pour les options de réservation */}
      <EditReservationOptionsModal
        isOpen={isReservationOptionsModalOpen}
        onClose={() => setIsReservationOptionsModalOpen(false)}
      />
    </div>
  )
}
